import type { ChatSource } from "@mirage/mosaics/Chat/types";
import i18n from "@mirage/translations";
import type { AccessType } from "./assist-api";
import type { ComposeAssistantConversationMessage } from "./assist-message";

export type PreConfiguredTemplateID =
  (typeof PRE_CONFIGURED_TEMPLATE_IDS)[number];

export interface BaseTemplate {
  id: string;
  name: string;
  description: string;
  templateContent: string;
  type: "custom" | "default";
}

export interface DefaultTemplate extends BaseTemplate {
  type: "default";
  contentType: AssistDefaultTemplateName;
}

export interface AssistCustomTemplate extends BaseTemplate {
  type: "custom";
  dataId: string;
  sources: ChatSource[];
  rev: number;
  accessType: AccessType;
  messagesHistory: ComposeAssistantConversationMessage[];
  isOwned: boolean;
}

export type AssistTemplate = AssistCustomTemplate | DefaultTemplate;

export enum AssistDefaultTemplateName {
  FAQ_DOCUMENT = "FAQ Document",
  TEAM_MEETING_AGENDA = "Team Meeting Agenda",
  PROJECT_KICKOFF = "Project Kickoff",
  MEETING_SUMMARY = "Meeting Summary",
  TASK_TRACKING = "Task Tracking",
  STATUS_REPORT = "Status Report",
  CHANGE_MANAGEMENT_PLAN = "Change Management Plan",
  TEAM_RECOGNITION_EMAIL = "Team Recognition Email",
  SOP = "Standard Operating Procedure (SOP)",
  FOLLOW_UP_EMAIL = "Follow-Up Email",
  MEETING_SCHEDULING_EMAIL = "Meeting Scheduling Email",
  CUSTOMER_ONBOARDING_EMAIL = "Customer Onboarding Email",
  SALES_OUTREACH_EMAIL = "Sales Outreach Email",
  MARKETING_EMAIL_CAMPAIGN = "Marketing Email Campaign",
  MARKETING_BLOG_POST = "Marketing Blog Post",
  SALES_CUSTOMER_ONBOARDING = "Sales Customer Onboarding Email",
  CUSTOMER_SUPPORT_EMAIL = "Customer Support Email",
  PRODUCT_RELEASE_NOTES = "Product Release Notes",
  ANALYSIS_REPORT = "Analysis Report",
  BUSINESS_MEMO = "Business Memo",
}

/**
 * This list of IDs must match whatever is on the server since this
 * ID is what is used to fetch the right prompt
 */
export const PRE_CONFIGURED_TEMPLATE_IDS = [
  "faq_document",
  "team_meeting_agenda",
  "project_kickoff",
  "meeting_summary",
  "task_tracking",
  "status_report",
  "change_management_plan",
  "team_recognition_email",
  "standard_operating_procedure_sop",
  "follow_up_email",
  "meeting_scheduling_email",
  "customer_onboarding_email",
  "sales_outreach_email",
  "marketing_email_campaign",
  "marketing_blog_post",
  "sales_customer_onboarding_email",
  "customer_support_email",
  "product_release_notes",
  "analysis_report",
  "business_memo",
] as const;

export const DEFAULT_PRECONFIGURED_TEMPLATE_ID: PreConfiguredTemplateID =
  PRE_CONFIGURED_TEMPLATE_IDS[0];

export function isPreConfiguredTemplateID(
  templateID: string,
): templateID is PreConfiguredTemplateID {
  return PRE_CONFIGURED_TEMPLATE_IDS.includes(
    templateID as PreConfiguredTemplateID,
  );
}

interface TemplateLabels {
  title: string;
  description: string;
}

// Helper to convert template enum name to base id key used in translations
function toBaseKey(templateName: AssistDefaultTemplateName): string {
  return templateName
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[()]/g, "")
    .replace(/-/g, "_");
}

// Lightweight wrapper around i18n.t to allow dynamic keys
const t = (key: string): string => {
  return (i18n as unknown as { t: (k: string) => string }).t(key);
};

export function getTemplateDisplayName(
  templateName: AssistDefaultTemplateName,
): string {
  const baseKey = toBaseKey(templateName);
  return t(`assist_templates_${baseKey}_title`);
}

export function getTemplateLabels(
  templateName: AssistDefaultTemplateName,
): TemplateLabels {
  const baseKey = toBaseKey(templateName);
  return {
    title: t(`assist_templates_${baseKey}_title`),
    description: t(`assist_templates_${baseKey}_description`),
  };
}

export function getTemplateContent(
  templateName: AssistDefaultTemplateName,
): string {
  const baseKey = toBaseKey(templateName);
  return t(`assist_templates_${baseKey}_content`);
}

export function createDefaultTemplates(): DefaultTemplate[] {
  return Object.values(AssistDefaultTemplateName).map((name) => {
    const labels = getTemplateLabels(name);
    return {
      id: toBaseKey(name),
      type: "default",
      name: labels.title,
      description: labels.description,
      templateContent: getTemplateContent(name),
      contentType: name,
    };
  });
}

export const templateIdToTemplateName: Record<
  PreConfiguredTemplateID,
  AssistDefaultTemplateName
> = {
  faq_document: AssistDefaultTemplateName.FAQ_DOCUMENT,
  team_meeting_agenda: AssistDefaultTemplateName.TEAM_MEETING_AGENDA,
  project_kickoff: AssistDefaultTemplateName.PROJECT_KICKOFF,
  meeting_summary: AssistDefaultTemplateName.MEETING_SUMMARY,
  task_tracking: AssistDefaultTemplateName.TASK_TRACKING,
  status_report: AssistDefaultTemplateName.STATUS_REPORT,
  change_management_plan: AssistDefaultTemplateName.CHANGE_MANAGEMENT_PLAN,
  team_recognition_email: AssistDefaultTemplateName.TEAM_RECOGNITION_EMAIL,
  standard_operating_procedure_sop: AssistDefaultTemplateName.SOP,
  follow_up_email: AssistDefaultTemplateName.FOLLOW_UP_EMAIL,
  meeting_scheduling_email: AssistDefaultTemplateName.MEETING_SCHEDULING_EMAIL,
  customer_onboarding_email:
    AssistDefaultTemplateName.CUSTOMER_ONBOARDING_EMAIL,
  sales_outreach_email: AssistDefaultTemplateName.SALES_OUTREACH_EMAIL,
  marketing_email_campaign: AssistDefaultTemplateName.MARKETING_EMAIL_CAMPAIGN,
  marketing_blog_post: AssistDefaultTemplateName.MARKETING_BLOG_POST,
  sales_customer_onboarding_email:
    AssistDefaultTemplateName.SALES_CUSTOMER_ONBOARDING,
  customer_support_email: AssistDefaultTemplateName.CUSTOMER_SUPPORT_EMAIL,
  product_release_notes: AssistDefaultTemplateName.PRODUCT_RELEASE_NOTES,
  analysis_report: AssistDefaultTemplateName.ANALYSIS_REPORT,
  business_memo: AssistDefaultTemplateName.BUSINESS_MEMO,
};
